import { useEffect, useState } from "react";
import { Package, Loader2, CheckCircle2, Plus, Link2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import api from "../../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(n || 0);

/**
 * Preview + confirm the "sync this invoice to inventory" flow.
 * Auto-creates ingredients that don't match (option c — full sync).
 *
 * Shows a grouped plan:
 *   - items that will increment an existing ingredient
 *   - items that will be auto-created
 * Then POSTs the sync and reports the outcome.
 */
export default function SyncToInventoryDialog({
  open, onClose, restaurantId, documentId, onSynced,
}) {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !documentId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPlan(null);
    api.previewDocumentInventorySync(restaurantId, documentId)
      .then((r) => { if (!cancelled) setPlan(r); })
      .catch((err) => { if (!cancelled) setError(err.message || "No se pudo previsualizar"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, documentId, restaurantId]);

  const apply = async ({ force = false } = {}) => {
    setApplying(true);
    try {
      const result = await api.syncDocumentToInventory(restaurantId, documentId, { force });
      const createdN = result.created.length;
      const base = `Inventario actualizado · ${result.matched.length} actualizados · ${createdN} creados`;
      const extra = createdN > 0
        ? " · la IA está completando los nuevos con merma y stock mín. en segundo plano"
        : "";
      toast.success(base + extra);
      onSynced?.(result);
      onClose();
    } catch (err) {
      if (String(err?.message || "").toLowerCase().includes("already")) {
        // Offer to force
        const go = window.confirm(
          "Este documento ya se sincronizó antes. ¿Volver a aplicarlo? (se duplicarán los movimientos)",
        );
        if (go) return apply({ force: true });
      }
      toast.error(err.message || "Error al sincronizar con inventario");
    } finally {
      setApplying(false);
    }
  };

  const matches = (plan?.plan || []).filter((p) => p.action === "match");
  const creates = (plan?.plan || []).filter((p) => p.action === "create");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sync-inventory-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Actualizar inventario con esta factura
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Preparando plan…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-900 text-sm">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && plan && plan.items_count === 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            Esta factura no tiene líneas de producto detectadas por el OCR.
            Sube la factura otra vez desde <strong>Inventario → Importar</strong>
            {" "}para reintentar la detección de líneas.
          </div>
        )}

        {!loading && plan && plan.items_count > 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[11px] uppercase tracking-wide text-emerald-800 font-semibold">
                  Se actualizan
                </div>
                <div className="text-2xl font-bold text-emerald-900">{plan.match_count}</div>
                <div className="text-xs text-emerald-800">ingredientes que ya tenías</div>
              </div>
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                <div className="text-[11px] uppercase tracking-wide text-sky-800 font-semibold">
                  Se crean nuevos
                </div>
                <div className="text-2xl font-bold text-sky-900">{plan.create_count}</div>
                <div className="text-xs text-sky-800">ingredientes que aún no tenías</div>
              </div>
            </div>

            {matches.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                  Ingredientes existentes ({matches.length})
                </h4>
                <ul className="divide-y rounded-md border">
                  {matches.map((p, i) => (
                    <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <Link2 className="h-4 w-4 text-emerald-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.match?.name || p.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          Factura: <span className="italic">{p.name}</span>
                        </div>
                      </div>
                      <div className="text-right text-xs shrink-0">
                        <div className="font-semibold">+{p.qty} {p.unit}</div>
                        <div className="text-muted-foreground">{fmtEUR(p.line_total)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {creates.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-xs font-semibold text-sky-900 uppercase tracking-wide">
                  Se crearán automáticamente ({creates.length})
                </h4>
                <ul className="divide-y rounded-md border">
                  {creates.map((p, i) => (
                    <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <Plus className="h-4 w-4 text-sky-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          Nuevo · unidad y precio de la factura · la IA completará merma y stock mín.
                        </div>
                      </div>
                      <div className="text-right text-xs shrink-0">
                        <div className="font-semibold">+{p.qty} {p.unit}</div>
                        <div className="text-muted-foreground">{fmtEUR(p.line_total)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {plan.already_synced && (
              <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-2">
                ⚠️ Este documento ya se sincronizó antes. Si aplicas de nuevo, se duplicarán los movimientos.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Cancelar
          </Button>
          <Button
            onClick={() => apply()}
            disabled={applying || loading || !plan || plan.items_count === 0}
            data-testid="confirm-sync-inventory-btn"
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> :
              <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Aplicar al inventario</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
